import React, { useCallback } from 'react';

import { FiltrosCompras, FiltrosDaLista, fetchEntregasDoFornecedor } from '../../../api/comprasDashboardApi';
import { formatarDataPura } from '../formatarData';
import SeloDeComentarios from '../../../components/comentarios/SeloDeComentarios';
import ModalDeDetalhe, { SUFIXO_FILTRADO, useDetalhePaginado } from './ModalDeDetalhe';

/**
 * Os pedidos que compõem a pontualidade de um fornecedor (at-110; fonte trocada na at-125).
 *
 * Aberto pelo nome do fornecedor na tabela do bloco "Entrega no prazo". Desde a at-125 cada linha
 * é um PEDIDO: a previsão registrada nele contra a data de entrada da sua última nota, pelo elo
 * pedido → nota da API do Nasajon (at-128).
 *
 * 🔴 A lista sai da MESMA `medirEntregas` que produz o percentual do card. Um segundo cruzamento
 * divergiria no primeiro ajuste de qualquer descarte, com a lista mostrando pedidos que o
 * percentual não conta — e é impossível, olhando a tela, saber qual dos dois está certo.
 *
 * ⚠️ **Os pedidos descartados não aparecem aqui**, e isso é dito no rodapé: sem previsão, ou com
 * previsão ou chegada anteriores à emissão, eles não foram medidos.
 *
 * ⚠️ O escopo do indicador (só oficina interna) é repetido no rodapé de propósito: quem chega aqui
 * pelo clique pode ter rolado a tela e perdido a faixa do card, e esta lista é justamente o lugar
 * onde alguém conclui "o fornecedor X atrasa".
 *
 * ⚠️ As datas são PURAS (chegada e prometido vêm como meia-noite UTC) — `formatarDataPura`, nunca
 * `new Date(...).toLocaleDateString`, que as mostraria um dia antes no fuso da oficina.
 */

const POR_PAGINA = 25;

interface Props {
  fornecedor: string;
  filtros: FiltrosCompras;
  onFechar: () => void;
}

const COLUNAS = ['Pedido', 'Notas fiscais', 'Entrada da última nota', 'Previsão do pedido', 'Diferença'];

/**
 * O texto da diferença, com o sinal por extenso.
 *
 * ⚠️ Nunca só o número: "-2" é ambíguo (dois dias antes? dois de atraso?), e é exatamente o campo
 * sobre o qual alguém vai cobrar um fornecedor.
 */
function textoDaDiferenca(dias: number): { texto: string; atrasada: boolean } {
  if (dias > 0) return { texto: `${dias} ${dias === 1 ? 'dia' : 'dias'} de atraso`, atrasada: true };
  if (dias === 0) return { texto: 'no dia prometido', atrasada: false };
  const adiantada = Math.abs(dias);
  return { texto: `${adiantada} ${adiantada === 1 ? 'dia' : 'dias'} antes`, atrasada: false };
}

export default function EntregasDoFornecedorModal({ fornecedor, filtros, onFechar }: Props) {
  const carregar = useCallback(
    (pagina: number, daLista: FiltrosDaLista) =>
      fetchEntregasDoFornecedor({ ...filtros, ...daLista, fornecedor, pagina, limite: POR_PAGINA }),
    [filtros, fornecedor],
  );

  const { dados, carregando, erro, pagina, setPagina, filtrosDaLista, aplicarFiltrosDaLista, filtrado } =
    useDetalhePaginado(carregar);

  const total = dados?.total ?? 0;
  const entregas = dados?.entregas ?? [];

  return (
    <ModalDeDetalhe
      titulo={`Pedidos entregues de ${fornecedor}`}
      legenda={
        carregando
          ? 'Carregando...'
          : erro
            ? 'Não foi possível carregar'
            : `${total.toLocaleString('pt-BR')} ${total === 1 ? 'pedido medido' : 'pedidos medidos'} · ${dados?.noPrazo ?? 0} no prazo${filtrado ? SUFIXO_FILTRADO : ''}`
      }
      carregando={carregando}
      erro={erro}
      erroTexto={
        'Não foi possível carregar os pedidos deste fornecedor. Isto não significa que não haja ' +
        'pedidos medidos — tente de novo e, se persistir, acione a TI.'
      }
      vazio={entregas.length === 0}
      vazioTexto={
        filtrado
          ? 'Nenhum pedido medido deste fornecedor com os filtros da lista. Ajuste ou limpe os filtros.'
          : 'Nenhum pedido deste fornecedor pôde ser medido no período.'
      }
      // at-90: o fornecedor já é o da linha, e a situação gerencial não é lida nesta medição.
      filtroDaLista={{
        campos: ['busca', 'placa', 'oficina', 'comprador', 'solicitante', 'periodo'],
        rotuloDaBusca: 'número do pedido ou da nota',
        rotuloDoPeriodo: 'Entrada da última nota',
        aplicados: filtrosDaLista,
        onAplicar: aplicarFiltrosDaLista,
      }}
      colunas={COLUNAS}
      classeTabela="dcr-tabela-entregas"
      paginacao={{
        pagina,
        ultimaPagina: Math.max(1, Math.ceil(total / POR_PAGINA)),
        onMudar: setPagina,
      }}
      rodape={
        <span>
          Só a oficina interna, e só o que pôde ser medido: pedido sem previsão, e previsão ou
          chegada anteriores à emissão do pedido, ficam de fora. O pedido conta como entregue na
          data da última nota.
        </span>
      }
      onFechar={onFechar}
    >
      {entregas.map((e, i) => {
        const diferenca = textoDaDiferenca(e.diasDeAtraso);
        const notas = e.notas.map((n) => n.nota).join(', ');
        return (
          <div key={`${e.pedido}-${i}`} className="dcr-linha">
            <div className="dcr-celula dcr-destaque">
              {e.pedido}
              <SeloDeComentarios total={e.comentarios} />
            </div>
            {/* Célula de uma linha: várias notas lado a lado, a lista inteira no title. */}
            <div className="dcr-celula" title={notas}>
              {notas || '-'}
            </div>
            <div className="dcr-celula">{formatarDataPura(e.chegada)}</div>
            <div className="dcr-celula">{formatarDataPura(e.prometido)}</div>
            <div className="dcr-celula" title={diferenca.texto}>
              {diferenca.atrasada ? (
                <span className="dcr-destaque">{diferenca.texto}</span>
              ) : (
                diferenca.texto
              )}
            </div>
          </div>
        );
      })}
    </ModalDeDetalhe>
  );
}
